import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/** `info` : « Pour info » de l'accueil inside (valeurs recopiées de la maquette, pacificBlue). */
export type StatusTone = 'ok' | 'warn' | 'error' | 'idle' | 'info';

/**
 * Canonical status pill for both apps — the integrator's dotted status pill (previously
 * copy-pasted inline in gateways/devices lists and duplicated as the global `.wh-status-pill`
 * SCSS in wheere-inside). Encapsulated styles + dark-mode via `:host-context(.dark)`.
 *
 * Usage: `<wh-status-pill tone="ok">ONLINE</wh-status-pill>` (label projected), or map a raw
 * status to a tone in the parent. Set `[dot]="false"` to hide the leading dot.
 */
@Component({
  selector: 'wh-status-pill',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="pill" [class]="'tone-' + tone()">
      @if (dot()) {
        <span class="dot"></span>
      }
      <ng-content />
    </span>
  `,
  styles: `
    :host {
      display: inline-flex;
    }
    .pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 3px 9px 3px 7px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 700;
      line-height: 1.3;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      white-space: nowrap;
    }
    .dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
    }
    .tone-ok {
      background: #def7ec;
      color: #03543f;
    }
    .tone-ok .dot {
      background: #31c48d;
      box-shadow: 0 0 0 3px rgba(49, 196, 141, 0.22);
    }
    .tone-warn {
      background: #fdf6b2;
      color: #723b13;
    }
    .tone-warn .dot {
      background: #e3a008;
      box-shadow: 0 0 0 3px rgba(227, 160, 8, 0.22);
    }
    .tone-error {
      background: #fde8e8;
      color: #9b1c1c;
    }
    .tone-error .dot {
      background: #f05252;
      box-shadow: 0 0 0 3px rgba(240, 82, 82, 0.22);
    }
    .tone-idle {
      background: #f3f4f6;
      color: #374151;
    }
    .tone-idle .dot {
      background: #9ca3af;
      box-shadow: 0 0 0 3px rgba(156, 163, 175, 0.22);
    }
    .tone-info {
      background: #e2effd;
      color: #0c4a9e;
    }
    .tone-info .dot {
      background: #107ded;
      box-shadow: 0 0 0 3px rgba(16, 125, 237, 0.22);
    }

    /* Dark mode — the class is toggled on <html> by the shared ThemeService. */
    :host-context(.dark) .tone-ok {
      background: rgba(49, 196, 141, 0.16);
      color: #6ee7b7;
    }
    :host-context(.dark) .tone-warn {
      background: rgba(227, 160, 8, 0.16);
      color: #fcd34d;
    }
    :host-context(.dark) .tone-error {
      background: rgba(240, 82, 82, 0.16);
      color: #fca5a5;
    }
    :host-context(.dark) .tone-idle {
      background: rgba(148, 163, 184, 0.16);
      color: #cbd5e1;
    }
    :host-context(.dark) .tone-info {
      background: rgba(16, 125, 237, 0.18);
      color: #93c5fd;
    }
  `,
})
export class WhStatusPillComponent {
  /** Colour bucket. */
  readonly tone = input<StatusTone>('idle');
  /** Show the leading glowing dot. */
  readonly dot = input<boolean>(true);

  /** Convenience: map a raw ONLINE/OFFLINE-style status to a tone. Not used by the template
   *  (callers can bind `[tone]="mapStatus(x)"`) but kept here as the single mapping source. */
  static toneFor(status: string | null | undefined): StatusTone {
    const s = (status ?? '').toUpperCase();
    if (s === 'ONLINE' || s === 'ACTIVE' || s === 'OK') return 'ok';
    if (s === 'OFFLINE' || s === 'ERROR' || s === 'SUSPENDED') return 'error';
    if (s === 'PENDING' || s === 'WARN' || s === 'WARNING') return 'warn';
    return 'idle';
  }
}
